import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { DashboardContext } from "@/context/DashboardContext";
import api from "@/lib/api";
import {
  Zap, LayoutDashboard, Wand2, PenLine, ShieldCheck, Users, Kanban,
  TrendingUp, Stethoscope, Sun, Bell, Activity, LogOut, Menu, X,
  Building2, Eye, ShieldQuestion, Plug, Gauge, Radar,
} from "lucide-react";

import Overview from "@/components/dashboard/Overview";
import CampaignBuilder from "@/components/dashboard/CampaignBuilder";
import Copywriter from "@/components/dashboard/Copywriter";
import Compliance from "@/components/dashboard/Compliance";
import LeadCentre from "@/components/dashboard/LeadCentre";
import CrmPipeline from "@/components/dashboard/CrmPipeline";
import Attribution from "@/components/dashboard/Attribution";
import CampaignDoctor from "@/components/dashboard/CampaignDoctor";
import CeoBrief from "@/components/dashboard/CeoBrief";
import Alerts from "@/components/dashboard/Alerts";
import Tracking from "@/components/dashboard/Tracking";
import Customers from "@/components/dashboard/Customers";
import MetaAds from "@/components/dashboard/MetaAds";
import AiOptimize from "@/components/dashboard/AiOptimize";
import Competitors from "@/components/dashboard/Competitors";

const ALL_NAV = [
  { id: "customers", key: "dash.customers", icon: Building2, C: Customers, adminOnly: true },
  { id: "overview", key: "dash.overview", icon: LayoutDashboard, C: Overview },
  { id: "integrations", key: "dash.integrations", icon: Plug, C: MetaAds, operatorOnly: true },
  { id: "competitors", key: "dash.competitors", icon: Radar, C: Competitors, operatorOnly: true },
  { id: "builder", key: "dash.builder", icon: Wand2, C: CampaignBuilder, operatorOnly: true },
  { id: "copywriter", key: "dash.copywriter", icon: PenLine, C: Copywriter, operatorOnly: true },
  { id: "compliance", key: "dash.compliance", icon: ShieldCheck, C: Compliance, operatorOnly: true },
  { id: "leads", key: "dash.leads", icon: Users, C: LeadCentre },
  { id: "crm", key: "dash.crm", icon: Kanban, C: CrmPipeline },
  { id: "attribution", key: "dash.attribution", icon: TrendingUp, C: Attribution },
  { id: "doctor", key: "dash.doctor", icon: Stethoscope, C: CampaignDoctor },
  { id: "optimize", key: "dash.optimize", icon: Gauge, C: AiOptimize, operatorOnly: true },
  { id: "brief", key: "dash.brief", icon: Sun, C: CeoBrief, operatorOnly: true },
  { id: "alerts", key: "dash.alerts", icon: Bell, C: Alerts },
  { id: "tracking", key: "dash.tracking", icon: Activity, C: Tracking },
];

export default function Dashboard() {
  const { t, lang, toggle } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, logout } = useAuth();
  const [active, setActive] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [viewCustomer, setViewCustomer] = useState(null);

  const displayUser = user || location.state?.user;
  const role = displayUser?.role || "Owner";
  const isAdmin = role === "Admin";

  useEffect(() => {
    if (!user && location.state?.user) setUser(location.state.user);
  }, [user, location.state, setUser]);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const switchRole = async () => {
    const next = isAdmin ? "Owner" : "Admin";
    try {
      await api.patch("/auth/role", { role: next });
      setUser({ ...(displayUser || {}), role: next });
      if (next === "Owner") { setViewCustomer(null); setActive("overview"); }
      toast.success(`Switched to ${next}`);
    } catch (e) { toast.error("Could not switch role"); }
  };

  const enterCustomerView = (c) => {
    setViewCustomer(c);
    setActive("overview");
    setSidebarOpen(false);
    toast.success(`Viewing ${c.name} (read-only)`);
  };

  const exitCustomerView = () => { setViewCustomer(null); setActive("customers"); };

  const viewSubset = ["overview", "leads", "crm", "attribution", "doctor", "alerts", "tracking"];
  const nav = ALL_NAV.filter((n) => {
    if (viewCustomer) return viewSubset.includes(n.id);
    if (n.adminOnly) return isAdmin;
    return true;
  });

  const ActiveComp = (nav.find((n) => n.id === active) || nav[0]).C;
  const activeKey = (nav.find((n) => n.id === active) || nav[0]).key;

  const ctxValue = {
    customerId: viewCustomer ? viewCustomer.id : null,
    readOnly: !!viewCustomer,
    customerName: viewCustomer ? viewCustomer.name : null,
    enterCustomerView,
  };

  return (
    <DashboardContext.Provider value={ctxValue}>
      <div className="min-h-screen bg-background text-foreground flex">
        <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-card/60 border-r border-border flex flex-col transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
          <div className="h-16 flex items-center gap-2 px-5 border-b border-border">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center"><Zap className="w-4 h-4 text-primary-foreground" fill="currentColor" /></div>
            <span className="font-display font-extrabold tracking-tight">AdsBoster<span className="text-primary"> AI</span></span>
          </div>
          <nav className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-1">
            {nav.map((n) => (
              <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => { setActive(n.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-200 ${active === n.id ? "bg-primary/15 text-primary font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"}`}>
                <n.icon className="w-4.5 h-4.5 shrink-0" /> {t(n.key)}
              </button>
            ))}
          </nav>
          <div className="p-3 border-t border-border space-y-1">
            <button data-testid="role-switcher" onClick={switchRole} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors duration-200">
              <ShieldQuestion className="w-4.5 h-4.5" /> {isAdmin ? t("dash.switchOwner") : t("dash.switchAdmin")}
            </button>
            <button data-testid="sidebar-logout" onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors duration-200">
              <LogOut className="w-4.5 h-4.5" /> {t("dash.logout")}
            </button>
          </div>
        </aside>

        {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-16 border-b border-border bg-card/40 backdrop-blur-md flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
            <div className="flex items-center gap-3">
              <button className="lg:hidden text-muted-foreground" onClick={() => setSidebarOpen((v) => !v)} data-testid="sidebar-toggle">
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <h1 className="font-display font-bold text-lg">{t(activeKey)}</h1>
            </div>
            <div className="flex items-center gap-3">
              {isAdmin && <span data-testid="admin-badge" className="hidden sm:inline text-[10px] font-mono font-bold px-2.5 py-1 rounded-full bg-primary/15 text-primary">ADMIN</span>}
              <button data-testid="dash-lang-toggle" onClick={toggle} className="text-xs font-mono px-2.5 py-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground transition-colors">{lang === "en" ? "BM" : "EN"}</button>
              <div className="flex items-center gap-2">
                {displayUser?.picture
                  ? <img src={displayUser.picture} alt="" className="w-8 h-8 rounded-full border border-border" />
                  : <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-bold">{displayUser?.name?.[0] || "U"}</div>}
                <div className="hidden sm:block leading-tight">
                  <p className="text-sm font-medium" data-testid="header-user-name">{displayUser?.name || "User"}</p>
                  <p className="text-[11px] text-muted-foreground">{role}</p>
                </div>
              </div>
            </div>
          </header>

          {viewCustomer && (
            <div data-testid="view-as-banner" className="bg-primary/10 border-b border-primary/30 px-4 sm:px-6 py-2.5 flex items-center justify-between">
              <p className="text-sm text-primary flex items-center gap-2"><Eye className="w-4 h-4" /> {t("dash.viewingAs")} <b>{viewCustomer.name}</b> · {t("dash.readOnly")}</p>
              <button data-testid="exit-view-as" onClick={exitCustomerView} className="text-xs font-semibold text-primary hover:text-primary/80 transition-colors">{t("dash.exitView")}</button>
            </div>
          )}

          <main className="flex-1 overflow-y-auto scrollbar-thin p-4 sm:p-6">
            <ActiveComp />
          </main>
        </div>
      </div>
    </DashboardContext.Provider>
  );
}
